import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatIDR } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { toast } from "sonner";
import { CheckCircle, ArrowRight } from "@phosphor-icons/react";

const PACKAGES = [
  { key: "basic", price: 1500000, capacity: "s.d. 20 tamu / hingga 3 jam" },
  { key: "standard", price: 3500000, capacity: "s.d. 40 tamu / hingga 5 jam" },
  { key: "premium", price: 7500000, capacity: "s.d. 60 tamu / seharian" },
];

export default function Events() {
  const { t } = useI18n();
  const [date, setDate] = useState(null);
  const [booked, setBooked] = useState([]);
  const [pkg, setPkg] = useState("standard");
  const [form, setForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", event_type: "", start_time: "18:00", end_time: "22:00", guest_count: 20, special_requests: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null);

  useEffect(() => {
    const d = new Date();
    const month = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    api.get("/reservations/availability", { params: { month } }).then((r) => setBooked(r.data.booked_dates));
  }, []);

  const disabledDays = (d) => {
    if (d < new Date(new Date().setHours(0, 0, 0, 0))) return true;
    const iso = d.toISOString().slice(0, 10);
    return booked.includes(iso);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!date) { toast.error("Pilih tanggal"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        guest_count: Number(form.guest_count),
        event_date: date.toISOString().slice(0, 10),
        package: pkg,
      };
      const r = await api.post("/reservations", payload);
      setSubmitted(r.data.reservation);
      toast.success(t("events.thanks"));
    } catch { toast.error("Gagal mengirim reservasi"); }
    finally { setSubmitting(false); }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto px-6 py-24 text-center">
        <CheckCircle size={64} weight="duotone" className="mx-auto text-[hsl(var(--sk-accent))]" />
        <p className="mt-6 label-eyebrow text-[hsl(var(--sk-muted-fg))]">Booking Number</p>
        <h1 className="mt-2 font-display text-5xl font-mono-stat" data-testid="submitted-booking-no">{submitted.booking_number}</h1>
        <p className="mt-6 text-[hsl(var(--sk-muted-fg))] max-w-md mx-auto">{t("events.thanks")}</p>
        <p className="mt-4 text-xs text-[hsl(var(--sk-muted-fg))]">Simpan nomor booking di atas — Anda akan menerima link untuk pembayaran deposit setelah dikonfirmasi.</p>
        <Button asChild size="lg" className="mt-8 rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90">
          <Link to={`/events/status/${submitted.booking_number}`} data-testid="go-check-status">
            Cek Status Reservasi <ArrowRight size={16} className="ml-2" />
          </Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-24">
      <div className="grid lg:grid-cols-12 gap-10 mb-16">
        <div className="lg:col-span-4">
          <p className="label-eyebrow text-[hsl(var(--sk-primary))]">{t("events.eyebrow")}</p>
        </div>
        <div className="lg:col-span-8">
          <h1 className="font-display text-5xl sm:text-6xl tracking-tighter">{t("events.title")}</h1>
          <p className="mt-6 text-lg text-[hsl(var(--sk-muted-fg))] max-w-2xl leading-relaxed">{t("events.intro")}</p>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-20">
        {PACKAGES.map((p) => (
          <button key={p.key} type="button" onClick={() => setPkg(p.key)} data-testid={`pkg-${p.key}`}
            className={`text-left p-8 rounded-2xl border-2 transition-colors ${pkg === p.key ? "border-[hsl(var(--sk-primary))] bg-white" : "border-[hsl(var(--sk-border))] bg-white/50 hover:border-[hsl(var(--sk-primary))]/50"}`}>
            <p className="label-eyebrow text-[hsl(var(--sk-primary))]">{t(`events.${p.key}`)}</p>
            <p className="mt-4 font-display text-4xl">{formatIDR(p.price)}</p>
            <p className="mt-3 text-sm text-[hsl(var(--sk-muted-fg))]">{p.capacity}</p>
          </button>
        ))}
      </div>

      <form onSubmit={submit} className="grid lg:grid-cols-2 gap-12 bg-white rounded-2xl p-8 lg:p-12 border border-[hsl(var(--sk-border))]">
        <div>
          <p className="label-eyebrow text-[hsl(var(--sk-primary))] mb-3">{t("events.availability")}</p>
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={disabledDays}
            data-testid="event-calendar"
            className="rounded-md border border-[hsl(var(--sk-border))] p-4"
          />
          {date && <p className="mt-4 text-sm">Selected: <span className="font-mono-stat">{date.toISOString().slice(0, 10)}</span></p>}
        </div>
        <div>
          <p className="label-eyebrow text-[hsl(var(--sk-primary))] mb-3">{t("events.form_title")}</p>
          <div className="space-y-4">
            <F label={t("checkout.name")} value={form.customer_name} onChange={(v) => setForm({ ...form, customer_name: v })} testid="ev-name" required />
            <div className="grid md:grid-cols-2 gap-4">
              <F label={t("checkout.email")} type="email" value={form.customer_email} onChange={(v) => setForm({ ...form, customer_email: v })} testid="ev-email" required />
              <F label={t("checkout.phone")} value={form.customer_phone} onChange={(v) => setForm({ ...form, customer_phone: v })} testid="ev-phone" required />
            </div>
            <F label={t("events.event_type")} value={form.event_type} onChange={(v) => setForm({ ...form, event_type: v })} testid="ev-type" required />
            <div className="grid grid-cols-3 gap-4">
              <F label={t("events.start")} type="time" value={form.start_time} onChange={(v) => setForm({ ...form, start_time: v })} testid="ev-start" />
              <F label={t("events.end")} type="time" value={form.end_time} onChange={(v) => setForm({ ...form, end_time: v })} testid="ev-end" />
              <F label={t("events.guests")} type="number" value={form.guest_count} onChange={(v) => setForm({ ...form, guest_count: v })} testid="ev-guests" />
            </div>
            <div>
              <Label className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{t("events.requests")}</Label>
              <Textarea value={form.special_requests} onChange={(e) => setForm({ ...form, special_requests: e.target.value })} className="mt-2" data-testid="ev-requests" />
            </div>
            <Button type="submit" size="lg" disabled={submitting} data-testid="submit-reservation"
              className="w-full rounded-full bg-[hsl(var(--sk-primary))] hover:bg-[hsl(var(--sk-primary))]/90">
              {submitting ? "…" : t("events.submit")}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

function F({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <Label className="label-eyebrow text-[hsl(var(--sk-muted-fg))]">{label}</Label>
      <Input type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required} className="mt-2" data-testid={testid} />
    </div>
  );
}
